import * as Icons from '@ant-design/icons';
import { Button, Space, Table } from 'antd';
import { CERTIFICATE_STATUS_MAP } from 'constants/certificate-status-map';
import { CourseInfoCard } from './CourseInfoCard';
import { CrudFilters, HttpError, useNotification } from '@refinedev/core';
import {
  ICertificateClassroomEnrollment,
  ICertificateEnrollment,
  ICourseCertificate
} from 'interfaces/course_certificate';
import { IClassroomEnrollment } from 'interfaces/classrooms';
import {
  IReadyForCertificateFilters,
  ReadyForCertificatesFilter
} from './ReadyForCertificatesFilter';
import {
  List,
  ShowButton,
  TagField,
  TextField,
  useTable
} from '@refinedev/antd';
import { StudentInfoCard } from './StudentInfoCard';
import { createFilters } from 'utils/create-filters';
import { generateURLWithFilters } from 'services/certificate';
import { getAPI } from 'requests/api';
import { getReportDownload, getReportStatus } from 'services/report';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate } from 'services/date';

const REPORT_STORAGE_KEY = 'certificate_report_id';

const ReadyForCertificatesTab = () => {
  const notification = useNotification();
  const [reportId, setReportId] = useState(
    localStorage.getItem(REPORT_STORAGE_KEY) || ''
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleSetReportId = useCallback((id: string) => {
    setReportId(id);
    localStorage.setItem(REPORT_STORAGE_KEY, id);
  }, []);

  const { tableProps, searchFormProps } = useTable<
    ICertificateEnrollment,
    HttpError,
    IReadyForCertificateFilters
  >({
    resource: 'graduate/courses/enrollments_for_certification',
    onSearch: (params): CrudFilters =>
      createFilters({
        search_user_name: params.search_user_name,
        by_cpf: params.by_cpf,
        by_user_email: params.by_user_email,
        by_course_code: params.by_course_code,
        by_classroom_id: params.by_classroom_id,
        by_certification_status: params.by_certification_status,
        completed_after: params.completed_after,
        completed_before: params.completed_before,
        certificate_updated_after: params.certificate_updated_after,
        certificate_updated_before: params.certificate_updated_before
      })
  });

  const handleDownloadCertificate = useCallback(async () => {
    try {
      const response = await getAPI().get(
        generateURLWithFilters<IReadyForCertificateFilters>(
          searchFormProps.form?.getFieldsValue(),
          'admin/graduate/courses/enrollments_for_certification/report'
        )
      );

      handleSetReportId(response.data.id);

      notification.open?.({
        type: 'success',
        message:
          'Relatório esta sendo gerado, e você será notificado quando estiver pronto para download.'
      });
    } catch {
      handleSetReportId('');
      notification.open?.({
        type: 'error',
        message: 'Download Error',
        description: 'Erro ao baixar o relatório de certificados.'
      });
    }
  }, [notification, searchFormProps, handleSetReportId]);

  useEffect(() => {
    if (!!reportId) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(async () => {
        try {
          const report = await getReportStatus(reportId);

          if (report.status === 'processed') {
            const documentUrl = await getReportDownload(
              report.files?.[0]?.id || ''
            );

            documentUrl.url && window.open(documentUrl.url, '_self');

            handleSetReportId('');
            clearInterval(intervalRef.current!);
          } else if (report.status === 'error') {
            handleSetReportId('');
            clearInterval(intervalRef.current!);
            notification.open?.({
              type: 'error',
              message: 'Download Error',
              description: 'Erro ao baixar o relatório de certificados.'
            });
          }
        } catch {
          handleSetReportId('');
          clearInterval(intervalRef.current!);

          notification.open?.({
            type: 'error',
            message: 'Download Error',
            description: 'Erro ao baixar o relatório de certificados.'
          });
        }
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [reportId, notification, handleSetReportId]);

  return (
    <List
      breadcrumb={null}
      title={''}
      canCreate={false}
      resource="graduate/courses/pending_certificates"
    >
      <ReadyForCertificatesFilter searchFormProps={searchFormProps} />
      <Space className="w-full justify-end mb-6">
        <Button
          onClick={handleDownloadCertificate}
          icon={<Icons.FileExcelOutlined />}
          disabled={!!reportId}
          loading={!!reportId}
        >
          {!!reportId
            ? 'Relatório já está sendo gerado'
            : 'Emitir relatório no Excel'}
        </Button>
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal(total, range) {
            return `${range[0]}-${range[1]} de ${total} itens`;
          }
        }}
      >
        <Table.Column<ICertificateEnrollment>
          dataIndex="user"
          key="student_info"
          fixed="left"
          width={250}
          title="Aluno"
          render={(user, record) => (
            <StudentInfoCard user={user} registration={record.registration} />
          )}
        />
        <Table.Column<ICertificateEnrollment>
          dataIndex="course"
          key="course"
          title="Curso"
          width={250}
          render={(course, record) => (
            <CourseInfoCard course={course} completedAt={record.completed_at} />
          )}
        />
        <Table.Column<IClassroomEnrollment[]>
          dataIndex="classroom_enrollments"
          key="classroom_enrollments"
          title="Turmas"
          width={300}
          render={(
            classroom_enrollments: ICertificateClassroomEnrollment[]
          ) => {
            if (classroom_enrollments.length === 0) {
              return <TextField value="-" />;
            }

            return (
              <Space wrap className="max-w-max">
                {classroom_enrollments.map(enrollment => (
                  <TagField
                    className="mr-0"
                    key={enrollment.id}
                    value={enrollment.classroom_code}
                  />
                ))}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="certificate_status"
          key="certificate_status"
          title="Status"
          width={100}
          render={certificate_status => {
            if (!certificate_status) {
              return <TagField color="orange" value="Pendente" />;
            }

            return (
              <TagField
                color={CERTIFICATE_STATUS_MAP[certificate_status].color}
                value={CERTIFICATE_STATUS_MAP[certificate_status].text}
              />
            );
          }}
        />
        <Table.Column
          dataIndex="certificate_updated_at"
          key="certificate_updated_at"
          title="Última atualização"
          width={120}
          sorter
          render={certificate_updated_at => {
            return <TextField value={formatDate(certificate_updated_at, 'DD/MM/YYYY')} />;
          }}
        />
        <Table.Column<ICourseCertificate>
          fixed="right"
          title="Ações"
          width={10}
          align="center"
          dataIndex="actions"
          render={(_, record) => {
            return (
              <ShowButton hideText size="small" recordItemId={record.id} />
            );
          }}
        />
      </Table>
    </List>
  );
};

export default ReadyForCertificatesTab;
